// src/pages/Login.js
import React from 'react';
import { useForm } from 'react-hook-form';
import { useLoginMutation } from '../redux/services/authApi';
import { useToast } from '../context/ToastContext';
import { useAuth } from '../context/AuthContext';
import {
  Box,
  Typography,
  TextField,
  Button,
  CircularProgress,
  Paper,
  Container,
  FormControl,
  Alert,
} from '@mui/material';
import { Navigate, Link as RouterLink } from 'react-router-dom';
import { isLoggedIn } from '../utils/auth';

const Login = () => {
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm();
  const [login, { data, isLoading, error }] = useLoginMutation();
  const { showToast } = useToast();
  const { setUser } = useAuth();

  // Redirect to profile if already logged in
  if (isLoggedIn()) return <Navigate to="/" />;

  const onSubmit = async (formData) => {
    try {
      const response = await login(formData).unwrap();
      if (response.token) {
        // Store user data in localStorage and context
        if (response.user) {
          localStorage.setItem('user', JSON.stringify(response.user));
          setUser(response.user);
        }
        showToast('Login successful!', 'success');
        window.location.href = '/';
      }
    } catch (err) {
      console.error('Login Error:', err);
      showToast('Login failed. Please check your credentials.', 'error');
    }
  };

  if (data) return <Navigate to="/" />;

  return (
    <Container maxWidth="sm">
      <Paper elevation={3} sx={{ maxWidth: 500, mx: 'auto', mt: 4, p: 4, borderRadius: 2 }}>
        <Typography variant="h4" component="h4" align="center" gutterBottom>
          Login to Salebot Connector
        </Typography>
        <form onSubmit={handleSubmit(onSubmit)}>
          <FormControl fullWidth sx={{ mb: 2 }} error={Boolean(errors.email)}>
            <TextField
              label="Email"
              type="email"
              placeholder="Enter your email"
              variant="outlined"
              fullWidth
              {...register('email', { required: 'Email is required' })}
              error={Boolean(errors.email)}
              helperText={errors.email?.message}
            />
          </FormControl>

          <FormControl fullWidth sx={{ mb: 3 }} error={Boolean(errors.password)}>
            <TextField
              label="Password"
              type="password"
              placeholder="Enter your password"
              variant="outlined"
              fullWidth
              {...register('password', { required: 'Password is required' })}
              error={Boolean(errors.password)}
              helperText={errors.password?.message}
            />
          </FormControl>

          <Button 
            type="submit" 
            variant="contained" 
            color="primary" 
            fullWidth 
            disabled={isLoading}
            sx={{ py: 1.5 }}
          >
            {isLoading ? <CircularProgress size={24} /> : 'Login'}
          </Button>
        </form>

        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error?.data?.message || 'Login failed. Please try again.'}
          </Alert>
        )}
        
        <Box sx={{ mt: 3, textAlign: 'center' }}>
          <Typography variant="body2">
            Don't have an account?{' '}
            <RouterLink to="/register" style={{ textDecoration: 'none' }}>
              Register here
            </RouterLink>
          </Typography>
        </Box>
      </Paper>
    </Container>
  );
};

export default Login;